import { AxiosRequestConfig } from 'axios';
import axios from './axios';

const uploadImageToS3 = (imageFile: File, type: 'workspace' | 'profile' | 'whitelabel'): Promise<string> => {
	return new Promise(async (resolve, reject) => {
		try {
			const config: AxiosRequestConfig = {
				method: 'post',
				url: '/composite/upload-on-s3',
				data: {
					fileName: imageFile.name,
					type,
				},
			};
			const resp = await axios(config);
			const uploadURL = resp.data.data;

			const imageUploadResp = await fetch(uploadURL, {
				method: 'PUT',
				headers: {
					'Content-Type': imageFile.type || 'image/jpg',
					'Access-Control-Allow-Origin': '*',
				},
				body: imageFile,
			});

			if (imageUploadResp.ok) {
				const imageUrl = uploadURL.split('?')[0];
				if (imageUrl) {
					// successful upload of image file
					resolve(imageUrl);
				} else {
					throw new Error('There was a problem uploading your image');
				}
			} else {
				throw new Error('There was a problem uploading your image');
			}
		} catch (error: any) {
			console.log('Uplaod image error', error);
			reject(error);
		}
	});
};

const uploadUserImage = (
	imageFile: File,
	type: 'IMAGE' | 'BGIMAGE' | 'COVER' | 'PUBLIC' | 'GIF',
	workspaceId: string,
	userId: string
): Promise<string> => {
	return new Promise(async (resolve, reject) => {
		try {
			const config: AxiosRequestConfig = {
				method: 'post',
				url: '/composite/upload-on-s3',
				data: {
					fileName: imageFile.name,
					type: 'card',
				},
			};
			const resp = await axios(config);
			const uploadURL = resp.data.data;

			const imageUploadResp = await fetch(uploadURL, {
				method: 'PUT',
				headers: {
					'Content-Type': imageFile.type || 'image/jpg',
					'Access-Control-Allow-Origin': '*',
				},
				body: imageFile,
			});

			if (imageUploadResp.ok) {
				const imageUrl = uploadURL.split('?')[0];
				if (imageUrl) {
					const userUploadConfig: AxiosRequestConfig = {
						method: 'post',
						url: '/myUploads',
						data: {
							location: imageUrl,
							workspaceId,
							userId,
							type,
						},
					};
					await axios(userUploadConfig);
					// successful upload of image file
					resolve(imageUrl);
				} else {
					throw new Error('There was a problem uploading your image');
				}
			} else {
				throw new Error('There was a problem uploading your image');
			}
		} catch (error: any) {
			console.log('Uplaod image error', error);
			reject(error);
		}
	});
};

const uploadUserVideo = (videoFile: File, workspaceId: string, userId: string): Promise<string> => {
	return new Promise(async (resolve, reject) => {
		try {
			const config: AxiosRequestConfig = {
				method: 'post',
				url: '/composite/upload-on-s3',
				data: {
					fileName: videoFile.name,
					type: 'card',
				},
			};
			const resp = await axios(config);
			const uploadURL = resp.data.data;

			const uploadResp = await fetch(uploadURL, {
				method: 'PUT',
				headers: {
					'Content-Type': videoFile.type || 'application/octet-stream',
					'Access-Control-Allow-Origin': '*',
				},
				body: videoFile,
			});
			if (!uploadResp.ok) throw new Error('There was a problem uploading your video');
			const fileUrl = uploadURL.split('?')[0];
			if (!fileUrl) throw new Error('There was a problem uploading your video');

			// Optionally record in user uploads as VIDEO (if backend supports)
			try {
				const userUploadConfig: AxiosRequestConfig = {
					method: 'post',
					url: '/myUploads',
					data: {
						location: fileUrl,
						workspaceId,
						userId,
						type: 'VIDEO',
					},
				};
				await axios(userUploadConfig);
			} catch (e) {
				// non-fatal
			}

			resolve(fileUrl);
		} catch (error: any) {
			console.log('Upload video error', error);
			reject(error);
		}
	});
};

const uploadUserAudio = (audioFile: File, workspaceId: string, userId: string): Promise<string> => {
	return new Promise(async (resolve, reject) => {
		try {
			const config: AxiosRequestConfig = {
				method: 'post',
				url: '/composite/upload-on-s3',
				data: {
					fileName: audioFile.name,
					type: 'card',
				},
			};
			const resp = await axios(config);
			const uploadURL = resp.data.data;

			const uploadResp = await fetch(uploadURL, {
				method: 'PUT',
				headers: {
					'Content-Type': 'audio/mpeg',
					'Access-Control-Allow-Origin': '*',
				},
				body: audioFile,
			});
			if (!uploadResp.ok) throw new Error('There was a problem uploading your audio');
			const fileUrl = uploadURL.split('?')[0];
			if (!fileUrl) throw new Error('There was a problem uploading your audio');

			// Record in user uploads as AUDIO
			try {
				const userUploadConfig: AxiosRequestConfig = {
					method: 'post',
					url: '/myUploads',
					data: {
						location: fileUrl,
						workspaceId,
						userId,
						type: 'AUDIO',
					},
				};
				await axios(userUploadConfig);
			} catch (e) {
				// non-fatal
			}

			resolve(fileUrl);
		} catch (error: any) {
			console.log('Upload audio error', error);
			reject(error);
		}
	});
};

const uploadPublicVideo = (videoFile: File): Promise<string> => {
	return new Promise(async (resolve, reject) => {
		try {
			const config: AxiosRequestConfig = {
				method: 'post',
				url: '/video-submissions/public-upload',
				data: {
					fileName: videoFile.name,
					contentType: videoFile.type || 'video/webm',
				},
			};
			const resp = await axios(config);
			const uploadURL = resp.data.data;

			const uploadResp = await fetch(uploadURL, {
				method: 'PUT',
				headers: {
					'Content-Type': videoFile.type || 'video/webm',
					'Access-Control-Allow-Origin': '*',
				},
				body: videoFile,
			});
			if (!uploadResp.ok) throw new Error('There was a problem uploading your video');
			const fileUrl = uploadURL.split('?')[0];
			if (!fileUrl) throw new Error('There was a problem uploading your video');

			resolve(fileUrl);
		} catch (error: any) {
			console.log('Upload video error', error);
			reject(error);
		}
	});
};

export const amazonUploadingServices = {
	uploadImageToS3,
	uploadUserImage,
	uploadUserVideo,
	uploadUserAudio,
	uploadPublicVideo,
};
